import { ROLL_RULES, isHit, isCritFumble, isFumble } from "~/rules/rolls";
import { evaluateFormulaWithValidation } from "~/utils/formulaEvaluator";
import type { RollItem } from "~/rules/rollableItems";

// Re-export from rules for backward compatibility
export { getAllRollableItems, type RollItem } from "~/rules/rollableItems";

/**
 * Regex for identifying roll tokens in text (e.g. "[Mind+1+1h]")
 */
export const ROLL_TOKEN_REGEX = /\[([^\]]+)\]/g;

/**
 * Parses a roll query string to extract formula parts and flat hits.
 * Supports multi-stat expressions like "Accuracy+Body+2" or "Body-Reflex+1h"
 * 
 * @example
 * parseRollQuery("Mind") -> { formula: "Mind", flatHits: 0 }
 * parseRollQuery("Stealth+2") -> { formula: "Stealth+2", flatHits: 0 }
 * parseRollQuery("Body+Mind-2+3h") -> { formula: "Body+Mind-2", flatHits: 3 }
 */
export const parseRollQuery = (query: string) => {
  // Extract flat hits (e.g., "+4h", "-2h")
  const flatHitsRegex = /([+-])\s*(\d+)\s*[hH]/g;
  let flatHits = 0;
  let match;

  while ((match = flatHitsRegex.exec(query)) !== null) {
    const sign = match[1] === '-' ? -1 : 1;
    const value = parseInt(match[2], 10) || 0;
    flatHits += sign * value;
  }

  // Remove flat hits from query to get the formula
  const formula = query.replace(/[+-]\s*\d+\s*[hH]/g, "").trim();

  return { formula, flatHits };
};

/**
 * Resolves a roll query string against available items and stats.
 * Supports both simple lookups (e.g., "Mind", "Stealth+2") and complex formulas (e.g., "Body+Mind-2+3h").
 * 
 * @param query - Roll query string like "Stealth+2+1h" or "Body+Mind-2+3h"
 * @param items - Available RollItems (skills, stats, movement)
 * @param coreStats - Optional map of stat names to values for evaluating formulas
 * @returns Resolved roll with name, pool, and flatHits, or null if cannot resolve
 */
export const resolveRoll = (
  query: string,
  items: RollItem[],
  coreStats?: Record<string, number>
): { name: string; pool: number; flatHits: number } | null => {
  const { formula, flatHits } = parseRollQuery(query);
  if (!formula) return null;

  // Try simple single-item lookup first (for backward compatibility)
  const singleItem = items.find((i) => i.name.toLowerCase() === formula.toLowerCase());
  if (singleItem) {
    let rollName = singleItem.name;
    if (flatHits !== 0) {
      rollName += `${flatHits > 0 ? "+" : ""}${flatHits} hit${Math.abs(flatHits) === 1 ? "" : "s"}`;
    }
    return { name: rollName, pool: singleItem.pool, flatHits };
  }

  // If not a single item, try to evaluate as a formula
  if (!coreStats) return null;

  // Build a combined stats map from items and coreStats
  const allStats: Record<string, number> = { ...coreStats };
  for (const item of items) {
    allStats[item.name] = item.pool;
  }

  // Use evaluateFormulaWithValidation to compute the pool and check validity
  const evaluation = evaluateFormulaWithValidation(formula, allStats);

  // If formula contains unknown stat names, return null to allow other matching (e.g., action tags)
  if (!evaluation.isValid) {
    return null;
  }

  const finalPool = Math.max(0, evaluation.result); // Clamp to 0

  // Build display name from formula
  let rollName = formula;
  if (flatHits !== 0) {
    rollName += `${flatHits > 0 ? "+" : ""}${flatHits} hit${Math.abs(flatHits) === 1 ? "" : "s"}`;
  }

  return { name: rollName, pool: finalPool, flatHits };
};

export interface RollResultData {
  rolls: number[];
  hits: number;
  isFumble: boolean;
  isCritFumble: boolean;
}

export const performRoll = (pool: number, flatHits: number = 0): RollResultData => {
  const rolls = Array.from({ length: pool }, () =>
    Math.floor(Math.random() * ROLL_RULES.dieType) + 1
  );

  const ones = rolls.filter(d => d === 1).length;
  const hits = rolls.filter(isHit).length + flatHits;
  const is_fumble = isFumble(hits, pool);
  const is_crit_fumble = isCritFumble(ones, pool);

  return {
    rolls,
    hits,
    isFumble: is_fumble && !is_crit_fumble,
    isCritFumble: is_crit_fumble,
  };
};
